import React, { forwardRef } from 'react';
import { Input, InputProps } from './Input';

export interface TextFieldProps extends Omit<InputProps, 'error' | 'errorMessage'> {
  /** Error state - compatible with MUI */
  error?: boolean;
  /** Helper text or error message - compatible with MUI */
  helperText?: string;
  /** Additional CSS classes for the icon */
  iconClassName?: string;
  /** Input props for start/end adornments - compatible with MUI */
  InputProps?: {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  };
  /** Label props - compatible with MUI */
  InputLabelProps?: {
    shrink?: boolean;
  };
  /** Make component required */
  required?: boolean;
}

/**
 * TextField Component
 * 
 * A Material-UI TextField compatible component that uses our atomic Input.
 * Provides the same API as MUI TextField for easy migration.
 * 
 * @example
 * ```tsx
 * // Basic usage (MUI-compatible)
 * <TextField
 *   label="Email"
 *   variant="outlined"
 *   fullWidth
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 * />
 * 
 * // With start adornment
 * <TextField
 *   label="Search"
 *   InputProps={{
 *     startAdornment: <Search className="w-5 h-5 text-gray-400" />
 *   }}
 * />
 * ```
 */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
  ({
    error = false,
    helperText,
    iconClassName,
    InputProps: _InputProps,
    InputLabelProps: _InputLabelProps,
    required,
    label,
    ...props
  }, ref) => {
    // Handle required label
    const displayLabel = required && label ? `${label} *` : label;

    // For now, we don't handle InputProps adornments - focus on core functionality
    // TODO: Add adornment support in future iterations

    return (
      <Input
        ref={ref}
        label={displayLabel}
        error={error}
        errorMessage={error ? helperText : undefined}
        helperText={!error ? helperText : undefined}
        required={required}
        iconClassName={iconClassName}
        {...props}
      />
    );
  }
);

TextField.displayName = 'TextField';
